import { Button, IconButton } from 'UI';
import { ManualTypeEnum } from 'api/generated';
import { CloseIcon } from 'assets/icons';
import PeoplePhoto from 'assets/images/upgrade-people.png';
import { useBundleUpsell } from 'hooks';
import { useMonetizationLightMode } from 'providers/MonetizationLightModeProvider/MonetizationLightModeContext';
import { FC, useEffect } from 'react';
import ReactModal from 'react-modal';
import { actions, selectors, useAppDispatch, useAppSelector } from 'store';
import styled, { DefaultTheme, css, keyframes } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

import UpgradeSubscriptionModal from '../UpgradeSubscriptionModal';
import { Bonus, MVP_BONUSES, PARTNER_BONUSES, isCertificationCourse } from './bonuses';
import { BonusCard } from './components/BonusCard';
import { SHORT_SCREEN_QUERY } from './layout';
import { useBonusUnlocks } from './useBonusUnlocks';

type BonusesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Only used for study materials, which keep their admin-authored bonus manual in the old modal. */
  manualType: ManualTypeEnum;
  /** The course whose bonuses these are. Defaults to the active course. */
  course?: { type?: string; bundleTier?: string | null } | null;
  /** MVP members can unlock the bonuses; everyone else is offered the upgrade. */
  isMember?: boolean;
  /** Analytics label for the upgrade buttons, e.g. "Get Bonuses". */
  referer?: string;
  /** Opened from the pricing modal itself: upgrading means going back to it, not reopening it. */
  hideUpgrade?: boolean;
};

const UPGRADE_LABEL = 'Upgrade to MVP';

/**
 * Full-screen bonuses popup, after certify's Bonuses mega menu: every bonus is the same card, each
 * with a live counter or an "Included" badge, an Unlock button and a link to its own website.
 * Sized to fit a laptop screen without scrolling; phones scroll.
 */
const BonusesModal: FC<BonusesModalProps> = ({
  isOpen,
  onClose,
  manualType,
  course,
  isMember = false,
  referer,
  hideUpgrade = false,
}) => {
  const dispatch = useAppDispatch();
  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const isDarkModeSetting = useAppSelector(selectors.settings.isDarkMode);
  const { forceLightMode } = useMonetizationLightMode();
  const { handleUpgradeBundle } = useBundleUpsell();
  const { statuses, errors, unlock } = useBonusUnlocks();

  const isDarkMode = !forceLightMode && isDarkModeSetting;
  const isIpta = isCertificationCourse(course ?? activeCourse);
  const showUpgrade = !isMember && !hideUpgrade;

  useEffect(() => {
    if (!isOpen || !isIpta) {
      return;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isIpta]);

  // These bonuses are IPTA's. Study systems sell different ones per course, so they keep their manual.
  if (!isIpta) {
    return (
      <UpgradeSubscriptionModal
        isOpen={isOpen}
        onClose={onClose}
        manualType={manualType}
        referer={referer}
        isBonusModal={isMember || hideUpgrade}
      />
    );
  }

  const handleUpgrade = async (label: string) => {
    onClose();
    // From the pricing modal, closing this popup is the way back to the plans.
    if (hideUpgrade) {
      return;
    }

    if (referer) {
      trackAmplitudeEvent(`${label} from ${referer} Pressed`);
    }

    if (activeCourse) {
      await handleUpgradeBundle(activeCourse);
    } else {
      dispatch(actions.courses.openChooseProductModalOpen());
    }
  };

  const handleUnlock = (bonus: Bonus) => {
    if (!isMember) {
      handleUpgrade(`Unlock ${bonus.name}`);
      return;
    }

    unlock(bonus);
  };

  const renderCards = (bonuses: Bonus[], areaPrefix: string) =>
    bonuses.map((bonus, index) => (
      <GridCard
        key={bonus.id}
        bonus={bonus}
        isMember={isMember}
        isDarkMode={isDarkMode}
        status={statuses[bonus.id]}
        error={errors[bonus.id]}
        onUnlock={handleUnlock}
        $area={`${areaPrefix}${index}`}
      />
    ));

  const upgradeButton = (
    <UpgradeButton variant="primary" size="small" onClick={() => handleUpgrade(UPGRADE_LABEL)}>
      {UPGRADE_LABEL}
    </UpgradeButton>
  );

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      contentLabel="MVP bonuses"
      style={{ overlay: { zIndex: 9999, backgroundColor: 'transparent' } }}
      $isDarkMode={isDarkMode}>
      <Header>
        <HeaderInner>
          <div>
            <Eyebrow>{isMember ? 'Your MVP bonuses' : 'Included with MVP'}</Eyebrow>
            <Title>MVP Bonuses</Title>
          </div>
          <HeaderActions>
            {showUpgrade && (
              <HeaderCta>
                <SocialProof>
                  <img src={PeoplePhoto} alt="" />
                  <span>Over 12,000 people have already upgraded and passed their exam</span>
                </SocialProof>
                {upgradeButton}
              </HeaderCta>
            )}
            <CloseButton iconComponent={<CloseIcon size={24} />} onClick={onClose} aria-label="Close bonuses" />
          </HeaderActions>
        </HeaderInner>
      </Header>

      <Body>
        <BodyInner>
          {/* One grid, not two columns, so each partner row lines up with the card beside it. On phones
              it falls back to source order: each heading, then its cards. */}
          <Grid>
            <ColumnLabel $area="partners">Strategic partnerships</ColumnLabel>
            {renderCards(PARTNER_BONUSES, 'partner')}
            <ColumnLabel $area="ours">Yours with the MVP</ColumnLabel>
            {renderCards(MVP_BONUSES, 'ours')}
          </Grid>

          {/* Phones have no room in the header, so the upgrade sits under the cards. */}
          {showUpgrade && <BottomCta>{upgradeButton}</BottomCta>}
        </BodyInner>
      </Body>
    </StyledModal>
  );
};

export default BonusesModal;

const paletteCSS = ({ $isDarkMode, theme: { colors } }: { $isDarkMode: boolean; theme: DefaultTheme }) =>
  $isDarkMode
    ? css`
        --bm-surface: ${colors.neutrals[11]};
        --bm-card: ${colors.neutrals[9]};
        --bm-card-hover: ${colors.neutrals[8]};
        --bm-border: ${colors.neutrals[8]};
        --bm-ink: ${colors.neutrals[1]};
        --bm-body: ${colors.neutrals[2]};
        --bm-muted: ${colors.neutrals[3]};
        --bm-pill-bg: ${colors.neutrals[6]};
        --bm-pill-ink: ${colors.neutrals[2]};
        --bm-unlocked-bg: rgba(74, 222, 128, 0.16);
        --bm-unlocked-ink: #4ade80;
        --bm-error: #f87171;
      `
    : css`
        --bm-surface: #ffffff;
        --bm-card: rgba(249, 250, 251, 0.6);
        --bm-card-hover: #ffffff;
        --bm-border: #e5e7eb;
        --bm-ink: #141826;
        --bm-body: #4b5563;
        --bm-muted: #6b7280;
        --bm-pill-bg: #e5e7eb;
        --bm-pill-ink: #4b5563;
        --bm-unlocked-bg: #dcfce7;
        --bm-unlocked-ink: #15803d;
        --bm-error: #dc2626;
      `;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledModal = styled(ReactModal)<{ $isDarkMode: boolean }>`
  ${paletteCSS};
  --bm-accent: ${({ theme: { colors } }) => colors.primary[1]};

  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  outline: none;
  font-family: 'Manrope', sans-serif;
  background: var(--bm-surface);
  animation: ${slideIn} 240ms cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ContentWidthCSS = css`
  width: 100%;
  max-width: 1040px;
  margin-inline: auto;
  padding-inline: 40px;

  ${respondToWidth.sm`
    padding-inline: 24px;
  `}

  ${respondToWidth.s`
    padding-inline: 16px;
  `}
`;

const Header = styled.header`
  flex-shrink: 0;
  border-bottom: 1px solid var(--bm-border);
`;

const HeaderInner = styled.div`
  ${ContentWidthCSS};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-block: 14px;

  ${SHORT_SCREEN_QUERY} {
    padding-block: 8px;
  }
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--bm-accent);

  ${SHORT_SCREEN_QUERY} {
    display: none;
  }
`;

// Same type scale as DefaultModal titles, in the page-title ink (as on Profile).
const Title = styled.h2`
  margin-top: 2px;
  ${({ theme: { typography } }) => typography.title_2_bold_32};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};

  ${({ theme: { typography } }) => respondToWidth.sm`
    ${typography.title_4_bold_24};
  `};

  ${SHORT_SCREEN_QUERY} {
    ${({ theme: { typography } }) => typography.title_4_bold_24};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const HeaderCta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  ${respondToWidth.sm`
    display: none;
  `}
`;

const SocialProof = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 300px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--bm-muted);

  img {
    flex-shrink: 0;
    height: 26px;
    width: auto;
  }

  ${respondToWidth.ls`
    display: none;
  `}
`;

const UpgradeButton = styled(Button)`
  width: auto;
  flex-shrink: 0;
  white-space: nowrap;
`;

const CloseButton = styled(IconButton)`
  flex-shrink: 0;

  svg path {
    fill: ${({ theme: { colors } }) => colors.neutrals[1]};
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

// Auto block margins centre the grid when the window has room to spare, and collapse to zero
// (never negative) when it does not, so nothing is clipped.
const BodyInner = styled.div`
  ${ContentWidthCSS};
  margin-block: auto;
  padding-block: 20px 24px;

  ${respondToWidth.s`
    padding-block: 16px 24px;
  `}

  ${SHORT_SCREEN_QUERY} {
    padding-block: 10px 12px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-areas:
    'partners ours'
    'partner0 ours0'
    'partner1 ours1'
    'partner2 ours2';
  align-items: stretch;
  gap: 14px 28px;

  ${respondToWidth.sm`
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: none;
  `}

  ${SHORT_SCREEN_QUERY} {
    gap: 8px 24px;
  }
`;

const gridAreaCSS = css<{ $area: string }>`
  grid-area: ${({ $area }) => $area};

  ${respondToWidth.sm`
    grid-area: auto;
  `}
`;

const ColumnLabel = styled.h3<{ $area: string }>`
  ${gridAreaCSS};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--bm-muted);

  ${respondToWidth.sm`
    &:not(:first-child) {
      margin-top: 12px;
    }
  `}
`;

const GridCard = styled(BonusCard)<{ $area: string }>`
  ${gridAreaCSS};
`;

const BottomCta = styled.div`
  display: none;
  margin-top: 24px;

  > button {
    width: 100%;
  }

  ${respondToWidth.sm`
    display: block;
  `}
`;
